/**
 * Shared SVG icon components for DerbyScores.
 * Stroke-based, consistent strokeWidth=1.8, rounded caps/joins.
 * All icons are 1em × 1em by default (size controlled by fontSize on parent).
 */
import React from 'react';

const defaults = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '1.8',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  style: { display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 },
};

function Icon({ size = '1em', children, ...extra }) {
  return (
    <svg {...defaults} width={size} height={size} {...extra}>
      {children}
    </svg>
  );
}

// ─── Fish / Fishing ──────────────────────────────────────────
export function IconFish({ size }) {
  return (
    <Icon size={size}>
      <path d="M6.5 12C6.5 9.5 9 6 14 6c4 0 7 3 7 6s-3 6-7 6c-5 0-7.5-3.5-7.5-6z" />
      <path d="M3 9l3.5 3L3 15" />
      <circle cx="16" cy="10.5" r="0.8" fill="currentColor" strokeWidth="0" />
    </Icon>
  );
}

// ─── Broadcast / Signal (live/active) ────────────────────────
export function IconBroadcast({ size }) {
  return (
    <Icon size={size}>
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <circle cx="12" cy="20" r="1" fill="currentColor" strokeWidth="0" />
    </Icon>
  );
}

// ─── Calendar / Upcoming ─────────────────────────────────────
export function IconCalendar({ size }) {
  return (
    <Icon size={size}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </Icon>
  );
}

// ─── Trophy / Past events ─────────────────────────────────────
export function IconTrophy({ size }) {
  return (
    <Icon size={size}>
      <path d="M6 2h12v8a6 6 0 0 1-12 0V2z" />
      <path d="M6 6H2a2 2 0 0 0 0 4h4" />
      <path d="M18 6h4a2 2 0 0 1 0 4h-4" />
      <line x1="12" y1="16" x2="12" y2="20" />
      <line x1="8" y1="20" x2="16" y2="20" />
    </Icon>
  );
}

// ─── Users / Organizers / Teams ───────────────────────────────
export function IconUsers({ size }) {
  return (
    <Icon size={size}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Icon>
  );
}

// ─── User / Participant ───────────────────────────────────────
export function IconUser({ size }) {
  return (
    <Icon size={size}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </Icon>
  );
}

// ─── Dollar / Revenue ─────────────────────────────────────────
export function IconDollar({ size }) {
  return (
    <Icon size={size}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </Icon>
  );
}

// ─── Monitor / Scoreboard ─────────────────────────────────────
export function IconMonitor({ size }) {
  return (
    <Icon size={size}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </Icon>
  );
}

// ─── Scale / Weigh-in / Catch Logs ───────────────────────────
export function IconScale({ size }) {
  return (
    <Icon size={size}>
      <path d="M12 3v18" />
      <path d="M6 6L2 16h8L6 6z" />
      <path d="M18 6l-4 10h8L18 6z" />
      <line x1="3" y1="21" x2="21" y2="21" />
    </Icon>
  );
}

// ─── Box / Box Weight ─────────────────────────────────────────
export function IconBox({ size }) {
  return (
    <Icon size={size}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </Icon>
  );
}

// ─── Target / Crosshair (Calcutta) ───────────────────────────
export function IconTarget({ size }) {
  return (
    <Icon size={size}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </Icon>
  );
}

// ─── Edit / Pencil ────────────────────────────────────────────
export function IconEdit({ size }) {
  return (
    <Icon size={size}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </Icon>
  );
}

// ─── Document / File / Invoice ────────────────────────────────
export function IconFile({ size }) {
  return (
    <Icon size={size}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </Icon>
  );
}

// ─── Warning / Alert Triangle ────────────────────────────────
export function IconWarning({ size }) {
  return (
    <Icon size={size}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </Icon>
  );
}

// ─── Info Circle ─────────────────────────────────────────────
export function IconInfo({ size }) {
  return (
    <Icon size={size}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </Icon>
  );
}

// ─── Check Circle / Success ───────────────────────────────────
export function IconCheckCircle({ size }) {
  return (
    <Icon size={size}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </Icon>
  );
}

// ─── Boat / Team Event ────────────────────────────────────────
export function IconBoat({ size }) {
  return (
    <Icon size={size}>
      <path d="M2 20h20" />
      <path d="M5 20V10l7-7 7 7v10" />
      <path d="M9 20v-6h6v6" />
    </Icon>
  );
}

// ─── Grid / Overview ─────────────────────────────────────────
export function IconGrid({ size }) {
  return (
    <Icon size={size}>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </Icon>
  );
}

// ─── Star / Sponsors ─────────────────────────────────────────
export function IconStar({ size }) {
  return (
    <Icon size={size}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </Icon>
  );
}

// ─── Bar Chart / Tournaments ─────────────────────────────────
export function IconBarChart({ size }) {
  return (
    <Icon size={size}>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </Icon>
  );
}

// ─── Arrow Right / Navigation ─────────────────────────────────
export function IconArrowRight({ size }) {
  return (
    <Icon size={size}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </Icon>
  );
}
